import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MessageSquare, Calendar, Video, Phone, Mail, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const Consultation = () => {
  const services = [
    {
      icon: MessageSquare,
      title: "Financial Planning",
      description: "Personalized strategies for your financial goals",
      topics: ["Retirement planning", "Investment strategy", "Estate planning", "Tax optimization"]
    },
    {
      icon: Video,
      title: "Business Advisory",
      description: "Expert guidance for business growth",
      topics: ["Business loans", "Cash flow management", "Expansion strategy", "Risk management"]
    },
    {
      icon: Calendar,
      title: "Wealth Management",
      description: "Comprehensive wealth building solutions",
      topics: ["Portfolio diversification", "Asset allocation", "Wealth preservation", "Inheritance planning"]
    },
    {
      icon: Phone,
      title: "Mortgage Consultation",
      description: "Find the right home loan for you",
      topics: ["Home buying process", "Loan pre-approval", "Rate comparison", "Refinancing options"]
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative bg-gradient-to-br from-teal-600 to-teal-800 text-white pt-32 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">Consultation Services</h1>
            <p className="text-lg sm:text-xl text-teal-100 max-w-3xl mx-auto mb-8">
              Get expert financial advice tailored to your unique situation. Our certified advisors are here to help you succeed
            </p>
            <Button asChild className="bg-white text-teal-600 hover:bg-teal-50">
              <Link to="/contact">Book Consultation <ArrowRight className="ml-2 w-4 h-4" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {services.map((service, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-6">
                    <service.icon className="w-8 h-8 text-teal-600" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{service.title}</h3>
                  <p className="text-gray-600 mb-6">{service.description}</p>
                  <h4 className="font-semibold mb-3">Topics Covered:</h4>
                  <ul className="space-y-2">
                    {service.topics.map((topic, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-teal-600 rounded-full mr-3" />
                        {topic}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How to Book */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">How to Book Your Consultation</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="text-center">
              <CardContent className="p-8">
                <Phone className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Call Us</h3>
                <p className="text-gray-600 mb-4">Speak directly with our team</p>
                <p className="font-semibold text-teal-600">+1 (555) 123-4567</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="p-8">
                <Mail className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Email Us</h3>
                <p className="text-gray-600 mb-4">Send your inquiry anytime</p>
                <p className="font-semibold text-teal-600">supportdesk@mgtrustx.com</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="p-8">
                <Calendar className="w-12 h-12 text-teal-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2">Online Form</h3>
                <p className="text-gray-600 mb-4">Fill out the contact form</p>
                <Button asChild variant="outline" className="border-teal-600 text-teal-600">
                  <Link to="/contact">Contact Form</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Consultation Types */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-16">Choose Your Consultation Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4">Phone Consultation</h3>
                <p className="text-3xl font-bold text-teal-600 mb-2">FREE</p>
                <p className="text-gray-600 mb-6">15-minute initial consultation</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-700">
                    <Clock className="w-4 h-4 mr-2 text-teal-600" />
                    15 minutes
                  </li>
                  <li className="flex items-center text-gray-700">
                    <Phone className="w-4 h-4 mr-2 text-teal-600" />
                    Phone call
                  </li>
                </ul>
                <Button asChild className="w-full">
                  <Link to="/contact">Book Now</Link>
                </Button>
              </CardContent>
            </Card>
            <Card className="border-2 border-teal-600">
              <CardContent className="p-8">
                <div className="bg-teal-600 text-white text-sm font-bold py-1 px-3 rounded-full inline-block mb-4">POPULAR</div>
                <h3 className="text-2xl font-bold mb-4">In-Person Meeting</h3>
                <p className="text-3xl font-bold text-teal-600 mb-2">FREE</p>
                <p className="text-gray-600 mb-6">60-minute comprehensive review</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-700">
                    <Clock className="w-4 h-4 mr-2 text-teal-600" />
                    60 minutes
                  </li>
                  <li className="flex items-center text-gray-700">
                    <MessageSquare className="w-4 h-4 mr-2 text-teal-600" />
                    In-office
                  </li>
                </ul>
                <Button asChild className="w-full bg-teal-600 hover:bg-teal-700">
                  <Link to="/contact">Book Now</Link>
                </Button>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4">Video Call</h3>
                <p className="text-3xl font-bold text-teal-600 mb-2">FREE</p>
                <p className="text-gray-600 mb-6">45-minute virtual consultation</p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center text-gray-700">
                    <Clock className="w-4 h-4 mr-2 text-teal-600" />
                    45 minutes
                  </li>
                  <li className="flex items-center text-gray-700">
                    <Video className="w-4 h-4 mr-2 text-teal-600" />
                    Zoom/Teams
                  </li>
                </ul>
                <Button asChild className="w-full">
                  <Link to="/contact">Book Now</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-teal-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">Ready to Get Expert Advice?</h2>
          <p className="text-lg sm:text-xl text-teal-100 mb-8">
            Book your free consultation today and take the first step towards financial success
          </p>
          <Button asChild size="lg" className="bg-white text-teal-600 hover:bg-teal-50">
            <Link to="/contact">Schedule Consultation</Link>
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Consultation;
